import React, { useRef, useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import ReactQuill from "react-quill";
import { map } from "lodash";
import { modules } from "../../constants/reactQuill";
import ImageUploadField from "./ImageUploadField";
import { FILETYPES, MAXSIZE } from "../../constants/challengeConstants";

function ScheduleItemSection({
  index,
  item,
  onChangeScheduleItem,
  options,
  habits,
  handleOnDropSchedule,
}) {
  const notifications = item?.notifications || [];
  const community = item?.community || [];
  const adminCommunity = item?.adminCommunity || [];
  const descriptionRef = useRef(false);
  const lessonTextRef = useRef(false);
  const [localDescription, setLocalDescription] = useState(
    item?.description || ""
  );
  const [localLessonText, setLocalLessonText] = useState(
    item?.lessonText || ""
  );

  // Keep local state in sync with parent when item changes (e.g., switching days)
  useEffect(() => {
    const newDescription = item?.description || "";
    console.log("Description useEffect triggered:", {
      old: localDescription,
      new: newDescription,
      index,
    });
    setLocalDescription(newDescription);
  }, [item?.description, index]);

  useEffect(() => {
    const newLessonText = item?.lessonText || "";
    console.log("LessonText useEffect triggered:", {
      old: localLessonText,
      new: newLessonText,
      index,
    });
    setLocalLessonText(newLessonText);
  }, [item?.lessonText, index]);

  // Helper to sync local Quill state to parent
  const syncQuillToParent = () => {
    if (localDescription !== item?.description) {
      onChangeScheduleItem(localDescription, "description", index);
    }
    if (localLessonText !== item?.lessonText) {
      onChangeScheduleItem(localLessonText, "lessonText", index);
    }
  };

  // Sync text immediately when it changes, not just on blur
  const handleDescriptionChange = (value) => {
    try {
      // Clean up the value to handle Google Docs formatting
      let cleanedValue = value;

      // If the value is just empty tags, set it to empty string
      if (value === "<p><br></p>" || value === "<p></p>") {
        cleanedValue = "";
      }

      // Ensure value is a string
      if (typeof cleanedValue !== "string") {
        cleanedValue = String(cleanedValue || "");
      }

      setLocalDescription(cleanedValue);
      onChangeScheduleItem(cleanedValue, "description", index);
    } catch (error) {
      console.error("Error in handleDescriptionChange:", error);
      // Fallback to empty string if there's an error
      setLocalDescription("");
      onChangeScheduleItem("", "description", index);
    }
  };

  const handleLessonTextChange = (value) => {
    try {
      // Clean up the value to handle Google Docs formatting
      let cleanedValue = value;

      // If the value is just empty tags, set it to empty string
      if (value === "<p><br></p>" || value === "<p></p>") {
        cleanedValue = "";
      }

      // Ensure value is a string
      if (typeof cleanedValue !== "string") {
        cleanedValue = String(cleanedValue || "");
      }

      // Log for debugging
      console.log("Lesson text changed:", {
        original: value,
        cleaned: cleanedValue,
        index: index,
        localLessonText: localLessonText,
      });

      setLocalLessonText(cleanedValue);
      onChangeScheduleItem(cleanedValue, "lessonText", index);
    } catch (error) {
      console.error("Error in handleLessonTextChange:", error);
      // Fallback to empty string if there's an error
      setLocalLessonText("");
      onChangeScheduleItem("", "lessonText", index);
    }
  };

  // Memoize the Select values to prevent infinite loops
  const activitiesValue = useMemo(() => {
    return (item?.activities || []).map((activity) => {
      const matchingOption = options.find(
        (option) => option.value === activity || option.value === activity.value
      );
      return (
        matchingOption || {
          value:
            typeof activity === "object"
              ? activity.value || activity.id || activity
              : activity,
          label:
            typeof activity === "object"
              ? activity.label || activity.title || activity
              : activity,
        }
      );
    });
  }, [item?.activities, options]);

  const habitsValue = useMemo(() => {
    return (item?.habits || []).map((habit) => {
      const matchingOption = options.find(
        (option) => option.value === habit || option.value === habit.value
      );
      return (
        matchingOption || {
          value:
            typeof habit === "object"
              ? habit.value || habit.id || habit
              : habit,
          label:
            typeof habit === "object"
              ? habit.title || habit.label || habit
              : habit,
        }
      );
    });
  }, [item?.habits, options]);

  const habitsOptions = useMemo(() => {
    return map(habits, (habit) => {
      return {
        value:
          typeof habit === "object" ? habit.value || habit.id || habit : habit,
        label:
          typeof habit === "object"
            ? habit.title || habit.label || habit
            : habit,
      };
    });
  }, [habits]);

  return (
    <div key={index}>
      {/* Test with just basic form controls first */}
      <Form.Group>
        <label>Schedule Title</label>
        <Form.Control
          value={item?.title || ""}
          placeholder="Schedule Title"
          type="string"
          onChange={(event) =>
            onChangeScheduleItem(event.target.value, "title", index)
          }
        />
      </Form.Group>
      <Form.Group>
        <label>Video Link</label>
        <Form.Control
          value={item?.videoLink || ""}
          placeholder="Video Link"
          type="string"
          onChange={(event) =>
            onChangeScheduleItem(event.target.value, "videoLink", index)
          }
        />
      </Form.Group>
      <Form.Group>
        <label>Schedule Description</label>
        <ReactQuill
          value={localDescription || ""}
          onChange={handleDescriptionChange}
          modules={modules}
          placeholder="Enter description..."
        />
      </Form.Group>
      <Form.Group>
        <label>Lesson Text</label>
        <ReactQuill
          value={localLessonText || ""}
          onChange={handleLessonTextChange}
          modules={modules}
          placeholder="Enter lesson text..."
        />
      </Form.Group>
      <Form.Group>
        <label>Activities</label>
        <Select
          key={`day-${index}-activities`}
          className="react-select info"
          classNamePrefix="react-select"
          placeholder="Choose Activities"
          name="multipleSelect"
          closeMenuOnSelect={false}
          isMulti
          value={activitiesValue}
          onChange={(selectedOptions) => {
            const activitiesValues = (selectedOptions || []).map((option) => {
              return typeof option === "object" ? option.value : option;
            });
            onChangeScheduleItem(activitiesValues, "activities", index);
          }}
          options={options}
        />
      </Form.Group>
      <Form.Group>
        <label>Habits</label>
        <Select
          key={`day-${index}-habits`}
          className="react-select info"
          classNamePrefix="react-select"
          placeholder="Choose Habits"
          name="multipleSelect"
          closeMenuOnSelect={false}
          isMulti
          value={habitsValue}
          onChange={(selectedOptions) => {
            const habitValues = (selectedOptions || []).map((option) => {
              return typeof option === "object" ? option.value : option;
            });
            onChangeScheduleItem(habitValues, "habits", index);
          }}
          options={habitsOptions}
        />
      </Form.Group>
      <Form.Group>
        <label>Notification Title</label>
        <Form.Control
          value={notifications[0]?.title || ""}
          placeholder="Notification Title"
          type="string"
          onChange={(event) =>
            onChangeScheduleItem(
              event.target.value,
              "notifications.0.title",
              index
            )
          }
        />
      </Form.Group>
      <Form.Group>
        <label>Notification Message</label>
        <Form.Control
          value={notifications[0]?.message || ""}
          placeholder="Notification Message"
          type="string"
          onChange={(event) =>
            onChangeScheduleItem(
              event.target.value,
              "notifications.0.message",
              index
            )
          }
        />
      </Form.Group>
      <Form.Group controlId="exampleForm.SelectCustom">
        <Form.Label>Time of Notification</Form.Label>
        <Form.Control
          as="select"
          value={notifications[0]?.time || 7}
          onChange={(event) =>
            onChangeScheduleItem(
              Number(event.target.value),
              "notifications.0.time",
              index
            )
          }
        >
          <option value={0}>Midnight</option>
          <option value={1}>1 AM</option>
          <option value={2}>2 AM</option>
          <option value={3}>3 AM</option>
          <option value={4}>4 AM</option>
          <option value={5}>5 AM</option>
          <option value={6}>6 AM</option>
          <option value={7}>7 AM</option>
          <option value={8}>8 AM</option>
          <option value={9}>9 AM</option>
          <option value={10}>10 AM</option>
          <option value={11}>11 AM</option>
          <option value={12}>Noon</option>
          <option value={13}>1 PM</option>
          <option value={14}>2 PM</option>
          <option value={15}>3 PM</option>
          <option value={16}>4 PM</option>
          <option value={17}>5 PM</option>
          <option value={18}>6 PM</option>
          <option value={19}>7 PM</option>
          <option value={20}>8 PM</option>
          <option value={21}>9 PM</option>
          <option value={22}>10 PM</option>
          <option value={23}>11 PM</option>
        </Form.Control>
      </Form.Group>
      {/* Community Post Text */}
      <Form.Group>
        <label>Community Post Text</label>
        <Form.Control
          value={
            community[0]?.communityPostText || item?.communityPostText || ""
          }
          placeholder="Community Post Text"
          type="string"
          onChange={(event) =>
            onChangeScheduleItem(
              event.target.value,
              "community.0.communityPostText",
              index
            )
          }
        />
      </Form.Group>
      {/* Community Post Image Upload */}
      <ImageUploadField
        label="Community Post Image"
        imageUrl={community[0]?.communityPostImage}
        onDrop={(files) =>
          handleOnDropSchedule && handleOnDropSchedule(index)(files)
        }
        onDelete={() =>
          onChangeScheduleItem("", "community.0.communityPostImage", index)
        }
        accept={FILETYPES}
        maxSize={MAXSIZE}
      />
      {/* Admin Community Post Text */}
      <Form.Group>
        <label>Admin Community Post Text</label>
        <Form.Control
          value={
            adminCommunity[0]?.adminCommunityPostText ||
            item?.adminCommunityPostText ||
            ""
          }
          placeholder="Admin Community Post Text"
          type="string"
          onChange={(event) =>
            onChangeScheduleItem(
              event.target.value,
              "adminCommunity.0.adminCommunityPostText",
              index
            )
          }
        />
      </Form.Group>
      {/* Admin Community Post Image Upload */}
      <ImageUploadField
        label="Admin Community Post Image"
        imageUrl={adminCommunity[0]?.adminCommunityPostImage}
        onDrop={(files) =>
          handleOnDropSchedule && handleOnDropSchedule(index, true)(files)
        }
        onDelete={() =>
          onChangeScheduleItem(
            "",
            "adminCommunity.0.adminCommunityPostImage",
            index
          )
        }
        accept={FILETYPES}
        maxSize={MAXSIZE}
      />
    </div>
  );
}

export default ScheduleItemSection;
